import type { HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { BearerScheme, HeaderKeyMap } from '@core/enums/http-requests.enum';
import { isWhitelistedEndpoint } from '@core/interceptors/utilities';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { RedirectService } from '@core/services/redirect.service';
import { TokenStore } from '@core/services/token.store';
import type { ResponseAuthenticationDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authentication/dtos/response.authentication.dto';
import { LoggerService } from '@kerberos-compliance/compliance-shared-ui';
import type { Observable } from 'rxjs';
import { catchError, finalize, from, shareReplay, switchMap, take, throwError } from 'rxjs';
import { AuthenticationFacade } from '../facades/authentication.facade';
import { TokenRefreshStateStore } from '../stores/token-refresh-state.store';

// Widened to `number` so comparisons against HttpErrorResponse.status share a type
const UnauthorizedStatus: number = HttpStatusCode.Unauthorized;

const authenticationEndpoints = [
  'authentication/token',
  'authentication/sign-in',
  'authentication/sign-in/client-credentials',
];

/**
 * Determines whether the given URL is an authentication request by checking if it matches any predefined authentication endpoints.
 *
 * @param {string} url - The URL to be checked against the authentication endpoints.
 * @returns {boolean} True if the URL matches an authentication endpoint, otherwise false.
 */
function isAuthenticationRequest(url: string): boolean {
  return authenticationEndpoints.some((endpoint) => url.includes(endpoint));
}

/**
 * Intercepts HTTP requests and responses to handle authentication and token management.
 *
 * This interceptor is responsible for:
 * - Adding Authorization headers with access tokens to outgoing requests.
 * - Handling unauthorized (401) errors by attempting token refresh and retrying failed requests.
 * - Managing token refresh state to avoid redundant refresh attempts.
 * - Initiating logout when authentication fails or tokens are invalid.
 *
 * Behavior:
 * - Requests to whitelisted endpoints or authentication-related endpoints are bypassed.
 * - If the authentication state is in a failed condition, all requests are blocked.
 * - Deduplicates requests during token refresh to prevent duplicate retries.
 * - Uses `TokenStore` for token management and `AuthenticationFacade` for token refresh.
 * - Redirects to the login page upon session expiration or authentication failure.
 *
 * Pipeline:
 * - Requests are cloned and enhanced with tokens when available.
 * - Unauthorized responses trigger a token refresh attempt:
 *   - If successful, retry the original request with the new token.
 *   - If unsuccessful, trigger a logout process.
 *
 * Key Functions:
 * - `handleLogout`: Clears session state, tokens, and redirects to the login page.
 * - `createAuthorizedRequest`: Clones and appends a token to the request headers.
 * - `getTokenRefresh`: Orchestrates the token refresh process and manages refresh state.
 *
 * Error Handling:
 * - Stops processing requests on authentication failure.
 * - Logs and handles errors related to token refresh or request retries.
 *
 * Dependencies:
 * - `AuthenticationFacade`: Handles authentication-related operations such as token refresh.
 * - `TokenStore`: Manages retrieval and validation of tokens.
 * - `RedirectService`: Stores and manages redirection URLs for post-login navigation.
 *
 * @param {HttpRequest<unknown>} request - The outgoing HTTP request to intercept.
 * @param {(req: HttpRequest<unknown>) => Observable<HttpEvent<unknown>>} next - The next handler in the interceptor chain.
 * @returns {Observable<HttpEvent<unknown>>} The HTTP event observable, potentially with token refresh handling.
 */
export const authenticationInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  // Skip interceptor for whitelisted endpoints and authentication requests
  if (isWhitelistedEndpoint(request.url) || isAuthenticationRequest(request.url)) {
    return next(request);
  }
  const tokenRefreshState = inject(TokenRefreshStateStore);
  const logger = inject(LoggerService);

  // If we're in a failed authentication state, immediately reject all requests
  if (tokenRefreshState.authenticationFailed()) {
    logger.debug(`Interceptor: Request blocked due to failed authentication state: ${request.url}`);
    return throwError(() => new Error('Authentication has failed, please log in again'));
  }

  const tokenStore = inject(TokenStore);
  const injector = inject(Injector);
  const authenticationFacade = injector.get(AuthenticationFacade);

  // Create a deduplication key for the request
  const requestKey = request.url;

  // Check if this exact request is already being processed during a refresh
  if (tokenRefreshState.inProgress() && tokenRefreshState.hasActiveRequest(requestKey)) {
    logger.debug(`Interceptor: Blocking duplicate request during refresh: ${requestKey}`);
    return throwError(() => new Error('Request already in progress during token refresh'));
  }

  /**
   * Clears session state, tokens, and redirects to the login page.
   *
   * `RedirectService` and `NavigationService` are resolved here instead of at the top of the
   * interceptor: their graph reaches the translation services, which issue an HTTP request while
   * being constructed. Injecting them on every request re-enters a root injector that may still
   * be hydrating them and fails with NG0200.
   *
   * @returns {Observable<never>} An observable that emits an error signaling session expiration.
   */
  const handleLogout = (): Observable<never> => {
    logger.warn('Interceptor: Initiating logout procedure');

    tokenRefreshState.setAuthenticationFailed(true);
    tokenRefreshState.resetRefreshState();

    tokenStore.clearTokens();

    const navigationService = injector.get(NavigationService);
    injector.get(RedirectService).setRedirectUrl(navigationService.getRouter().url);
    void navigationService.navigateTo('login');

    return throwError(() => new Error('Session expired, redirecting to login'));
  };

  const createAuthorizedRequest = (token: string, originalRequest: HttpRequest<unknown>): HttpRequest<unknown> =>
    originalRequest.clone({
      setHeaders: { [HeaderKeyMap.Authorization]: `${BearerScheme} ${token}` },
    });

  /**
   * Retrieves or initiates a token refresh operation to renew authentication tokens.
   * Ensures refresh requests are not repeated more frequently than the minimum
   * allowable interval and prevents multiple concurrent refresh operations by
   * using a shared observable.
   *
   * @returns {Observable<ResponseAuthenticationDto | undefined>} An observable emitting the refreshed token or `undefined` on failure.
   */
  const getTokenRefresh = (): Observable<ResponseAuthenticationDto | undefined> => {
    // Only reuse the existing observable if refresh is in progress
    // This prevents using a completed observable from a previous refresh
    if (tokenRefreshState.inProgress() && tokenRefreshState.refreshObservable()) {
      logger.debug('Interceptor: Using existing token refresh');
      return tokenRefreshState.refreshObservable()!;
    }

    // Clear any existing observable that might be completed
    tokenRefreshState.setRefreshObservable(undefined);

    // Check if we've refreshed recently to avoid hammering the server
    const now = Date.now();
    const timeSinceLastRefresh = now - tokenRefreshState.lastRefreshTimestamp();
    if (tokenRefreshState.lastRefreshTimestamp() > 0 && timeSinceLastRefresh < tokenRefreshState.minRefreshInterval) {
      logger.debug(
        `Interceptor: Too soon to refresh again (${timeSinceLastRefresh}ms < ${tokenRefreshState.minRefreshInterval}ms)`,
      );
      return throwError(() => new Error('Too many refresh attempts, please try again later'));
    }

    // Start a new refresh
    logger.debug('Interceptor: Starting new token refresh');
    tokenRefreshState.setInProgress(true);
    tokenRefreshState.setAuthenticationFailed(false);

    const refreshObs$ = from(
      authenticationFacade
        .refreshSession()
        .then((result): ResponseAuthenticationDto | undefined => {
          if (result?.access_token) {
            tokenRefreshState.setLastRefreshTimestamp(Date.now());
            tokenRefreshState.setAuthenticationFailed(false);
            logger.debug('Interceptor: Token refresh successful');
          } else {
            logger.warn('Interceptor: No token received from refresh');
          }
          return result;
        })
        .catch((error: unknown): ResponseAuthenticationDto | undefined => {
          logger.warn('Interceptor: Token refresh failed', error instanceof Error ? error.message : String(error));
          return undefined;
        }),
    ).pipe(
      shareReplay({ bufferSize: 1, refCount: true }),
      finalize(() => {
        logger.debug('Interceptor: Refresh complete, cleaning up state');
        tokenRefreshState.setInProgress(false);
        tokenRefreshState.clearActiveRequests();
      }),
    );

    tokenRefreshState.setRefreshObservable(refreshObs$);

    return refreshObs$;
  };

  let clonedRequest = request;
  if (tokenStore.hasValidToken()) {
    const authHeader = tokenStore.getAuthorizationHeader();
    if (authHeader) {
      clonedRequest = request.clone({
        setHeaders: { [HeaderKeyMap.Authorization]: authHeader },
      });
    }
  }

  const handleRetryError = (retryError: unknown): Observable<HttpEvent<unknown>> => {
    // If we still get 401 after refresh, give up and logout
    if (retryError instanceof HttpErrorResponse && retryError.status === UnauthorizedStatus) {
      logger.warn(`Interceptor: Still receiving 401 after token refresh for ${clonedRequest.url}`);
      return handleLogout();
    }
    return throwError(() => retryError);
  };

  const cleanupRequest = (): void => {
    logger.debug(`Interceptor: Request complete: ${requestKey}`);
    tokenRefreshState.removeActiveRequest(requestKey);
  };

  const retryRequestWithNewToken = (
    tokenResponse: ResponseAuthenticationDto | undefined,
  ): Observable<HttpEvent<unknown>> => {
    if (!tokenResponse?.access_token) {
      return handleLogout();
    }

    logger.debug(`Interceptor: Retrying request with new token: ${clonedRequest.url}`);
    const retriedRequest = createAuthorizedRequest(tokenResponse.access_token, clonedRequest);

    return next(retriedRequest).pipe(catchError(handleRetryError), finalize(cleanupRequest));
  };

  const handleRefreshError = (refreshError: unknown): Observable<HttpEvent<unknown>> => {
    const errorMessage = refreshError instanceof Error ? refreshError.message : String(refreshError);
    logger.warn('Interceptor: Error during refresh', errorMessage);
    tokenRefreshState.removeActiveRequest(requestKey);
    return handleLogout();
  };

  const handleUnauthorizedError = (error: unknown): Observable<HttpEvent<unknown>> => {
    if (
      !(error instanceof HttpErrorResponse) ||
      error.status !== UnauthorizedStatus ||
      isAuthenticationRequest(clonedRequest.url)
    ) {
      return throwError(() => error);
    }

    logger.debug(`Interceptor: 401 received for ${clonedRequest.url}`);

    // Track this request to prevent duplicates during refresh
    tokenRefreshState.addActiveRequest(requestKey);

    return getTokenRefresh().pipe(take(1), switchMap(retryRequestWithNewToken), catchError(handleRefreshError));
  };

  return next(clonedRequest).pipe(catchError(handleUnauthorizedError));
};
